import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import {
  getConversationReminder,
  formatReminderDueTime,
} from 'dashboard/helper/reminderHelper';

/**
 * Reads and writes the reminder on the currently selected conversation.
 * Shared by the composer button and the banner so both behave identically.
 *
 * @returns {{reminder: import('vue').ComputedRef, isSaving: import('vue').Ref,
 *   setReminder: Function, clearReminder: Function}}
 */
export function useReminder() {
  const store = useStore();
  const { t } = useI18n();
  const currentChat = useMapGetter('getSelectedChat');

  const isSaving = ref(false);
  const reminder = computed(() => getConversationReminder(currentChat.value));

  /**
   * @param {{snoozedUntil: number, note: string}} payload snoozedUntil is unix seconds
   * @returns {Promise<boolean>} whether it saved
   */
  const setReminder = async ({ snoozedUntil, note }) => {
    isSaving.value = true;
    try {
      await store.dispatch('setConversationReminder', {
        conversationId: currentChat.value.id,
        snoozedUntil,
        note,
      });
      useAlert(
        t('CONVERSATION.REMINDER.SET', {
          time: formatReminderDueTime(
            new Date(snoozedUntil * 1000).toISOString()
          ),
        })
      );
      return true;
    } catch (error) {
      useAlert(t('CONVERSATION.REMINDER.ERROR'));
      return false;
    } finally {
      isSaving.value = false;
    }
  };

  /**
   * Removing the reminder reopens the conversation, since it is no longer parked.
   *
   * @returns {Promise<boolean>} whether it cleared
   */
  const clearReminder = async () => {
    isSaving.value = true;
    try {
      await store.dispatch('clearConversationReminder', {
        conversationId: currentChat.value.id,
      });
      useAlert(t('CONVERSATION.REMINDER.CLEARED'));
      return true;
    } catch (error) {
      useAlert(t('CONVERSATION.REMINDER.ERROR'));
      return false;
    } finally {
      isSaving.value = false;
    }
  };

  return { reminder, isSaving, setReminder, clearReminder };
}
